import { HttpInterceptorFn, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { throwError, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggerService } from '../services/logger.service';
import { TokenStorageService } from '../services/token-storage.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const logger = inject(LoggerService);
  const http = inject(HttpClient);
  const tokenStorage = inject(TokenStorageService);

  // Add auth header with jwt if user is logged in and request is to api url
  const token = tokenStorage.getToken();
  const isApiUrl = req.url.includes('/api/') || req.url.startsWith('http');
  const isLoginCall = req.url.includes('/Auth/login');
  const isRefreshCall = req.url.includes('/Auth/refresh');

  let authReq = req;
  if (token && isApiUrl && !isLoginCall && !isRefreshCall) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      logger.error(`HTTP Error ${error.status}`, {
        url: req.url,
        method: req.method,
        error: error.error
      }, 'AuthInterceptor');

      if (error.status === 401) {
        // Allow silent refresh for /Auth/me, but not for other /Auth/* endpoints
        const isMeCall = req.url.includes('/Auth/me');
        if (req.url.includes('/Auth/') && !isMeCall) {
          return throwError(() => error);
        }

        // If refresh itself failed, stop looping, logout and propagate error
        if (isRefreshCall) {
          tokenStorage.clear();
          router.navigate(['/login']);
          return throwError(() => error);
        }

        // Try silent refresh once
        return http.post<any>(`${environment.apiUrl}/Auth/refresh`, {}, { withCredentials: true }).pipe(
          switchMap((refreshResponse) => {
            const newToken = refreshResponse?.accessToken;
            const expiresAt = refreshResponse?.expiresAt;
            if (newToken) {
              // Сохраняем новый токен через TokenStorageService
              tokenStorage.setToken(newToken);
              if (expiresAt) {
                tokenStorage.setTokenExpiry(expiresAt);
              }
              const retried = authReq.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
              return next(retried);
            }
            tokenStorage.clear();
            router.navigate(['/login']);
            return throwError(() => new Error('Unauthorized'));
          }),
          catchError((e) => {
            tokenStorage.clear();
            router.navigate(['/login']);
            return throwError(() => e);
          })
        );
      }

      if (error.status === 403) {
        logger.warn('Access forbidden - insufficient permissions', {
          url: req.url,
          method: req.method
        }, 'AuthInterceptor');
      }

      return throwError(() => error);
    })
  );
};